/* eslint-disable react/require-default-props */
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FaBarcode, FaTrashAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';

import ProductNameSearch from '../ProductNameSearch';
import QuantityStepper from './QuantityStepper';
import { ControlScope } from '../../styles/ui/Composer';
import { CtaButton, IconButton } from '../../styles/ui/Button';
import { TableWrapper } from '../../styles/table/TableWrapper';
import { DataTable } from '../../styles/ui/Table';
import { EmptyState } from '../../styles/feedback/EmptyState';

// ===========================================================================
// Editor da lista de itens do MOVIMENTO — compartilhado pela entrada, pela
// saída e pelo editor (2026-09-12, redesenho). As três telas tinham a mesma
// seção triplicada, com divergências acumuladas (o editor não checava PDV
// antes de adicionar, os títulos de estado vazio diferiam, a saída repetia o
// `handleAddItem` inteiro).
//
// A lista é CONTROLADA pelo pai (`items`/`onChange`): é o estado que vai no
// payload — no PUT ela é a substituição integral da lista atual (0093 §3).
//
// Mudança de comportamento deliberada do redesenho: a resolução do produto
// deixou de ser "digite o SKU exato e clique na lupa"
// (`GET /products/sku/:sku`) e passou ao autocomplete compartilhado
// (`ProductNameSearch`, `GET /products/search-name`), como no protótipo e como
// já fazem Transferências e a busca da listagem. O fluxo de bipagem segue
// intacto: SKU de 7 dígitos resolve direto, sem passar pela lista.
//
// A forma do item preserva as chaves que as telas já usavam
// (`prod_id`/`prod_qtd`/`sku`/`prodName`) e PRESERVA extras desconhecidos
// (`unit_cost`/`total_cost` do prefill de XML) ao somar quantidade.
// ===========================================================================

const AddRow = styled.div`
   display: flex;
   align-items: flex-end;
   gap: 12px;
   flex-wrap: wrap;
   margin-bottom: 16px;
`;

// Coluna de um controle da linha de adição. `$grow` na busca de produto (é ela
// que absorve a largura sobrando); largura natural nos demais.
const AddCell = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
   min-width: 0;
   flex: ${({ $grow }) => ($grow ? '1 1 260px' : '0 0 auto')};
`;

const AddLabel = styled.span`
   font-size: 11.5px;
   font-weight: 600;
   color: ${({ theme }) => theme.colors.textMuted};
`;

const ScanHint = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   margin-top: 10px;
   font-size: 11.5px;
   color: ${({ theme }) => theme.colors.textMuted};
`;

const TotalRow = styled.tr`
   td {
      background: #fafafb;
      font-weight: 800;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
   }
`;

export default function MovementItemsEditor({
   items,
   onChange,
   disabled = false,
   requirePdv = true,
   pdvId = '',
   emptyTitle = 'Nenhum item adicionado',
   emptyDescription = 'Bipe o SKU ou busque pelo nome do produto para começar.',
}) {
   const [productQuery, setProductQuery] = useState('');
   const [selectedProduct, setSelectedProduct] = useState(null);
   const [quantity, setQuantity] = useState('1');

   const quantityRef = useRef(null);

   const totalUnits = items.reduce(
      (total, item) => total + (Number(item.prod_qtd) || 0),
      0
   );

   const handleAdd = () => {
      if (requirePdv && !pdvId) {
         toast.error('Selecione o PDV antes de adicionar itens.');
         return;
      }
      if (!selectedProduct) {
         toast.error('Selecione um produto na busca.');
         return;
      }

      const qty = Number.parseInt(quantity, 10);
      if (!Number.isFinite(qty) || qty <= 0) {
         toast.error('Informe uma quantidade maior que zero.');
         return;
      }

      const prodId = Number(selectedProduct.id ?? selectedProduct.prod_id);
      const existingIndex = items.findIndex(
         (item) => Number(item.prod_id) === prodId
      );

      // Produto repetido soma quantidade (UX preservada das três telas) e
      // mantém os campos extras que a linha já carregava.
      if (existingIndex >= 0) {
         const next = [...items];
         next[existingIndex] = {
            ...next[existingIndex],
            prod_qtd: Number(next[existingIndex].prod_qtd || 0) + qty,
         };
         onChange(next);
      } else {
         onChange([
            ...items,
            {
               prod_id: prodId,
               prod_qtd: qty,
               sku: String(selectedProduct.sku ?? selectedProduct.prod_sku ?? ''),
               prodName:
                  selectedProduct.name ?? selectedProduct.prod_name ?? '',
            },
         ]);
      }

      setProductQuery('');
      setSelectedProduct(null);
      setQuantity('1');
   };

   const handleQtyKeyDown = (event) => {
      if (event.key === 'Enter') {
         event.preventDefault();
         handleAdd();
      }
   };

   // Edição in-place por ÍNDICE (não por prod_id): a lista pode chegar do
   // prefill de XML com a mesma chave repetida, e trocar a quantidade da linha
   // errada seria silencioso.
   const handleRowQtyChange = (index, rawValue) => {
      const next = items.map((item, idx) =>
         idx === index ? { ...item, prod_qtd: rawValue } : item
      );
      onChange(next);
   };

   const handleRemove = (index) => {
      onChange(items.filter((_, idx) => idx !== index));
   };

   return (
      <>
         <AddRow>
            <AddCell $grow>
               <AddLabel>SKU ou nome do produto</AddLabel>
               <ControlScope>
                  <ProductNameSearch
                     value={productQuery}
                     onChange={setProductQuery}
                     onSelect={(product) => {
                        setSelectedProduct(product || null);
                        if (product) quantityRef.current?.focus();
                     }}
                     nameLike
                     displayMode="sku-name"
                     placeholder="Bipe o SKU (7 dígitos) ou busque pelo nome..."
                     disabled={disabled}
                  />
               </ControlScope>
            </AddCell>

            <AddCell>
               <AddLabel>Quantidade</AddLabel>
               <QuantityStepper
                  value={quantity}
                  onChange={setQuantity}
                  disabled={disabled}
                  inputRef={quantityRef}
                  onKeyDown={handleQtyKeyDown}
                  ariaLabel="Quantidade do item a adicionar"
               />
            </AddCell>

            <AddCell>
               <CtaButton
                  type="button"
                  onClick={handleAdd}
                  disabled={disabled || !selectedProduct || !quantity}
                  title={
                     selectedProduct
                        ? 'Adicionar item à lista'
                        : 'Selecione um produto na busca'
                  }
               >
                  Adicionar
               </CtaButton>
            </AddCell>
         </AddRow>

         {items.length === 0 ? (
            <EmptyState
               icon={<FaBarcode size={20} />}
               title={emptyTitle}
               description={emptyDescription}
            />
         ) : (
            <TableWrapper $minWidth="0">
               <DataTable>
                  <thead>
                     <tr>
                        <th className="center">SKU</th>
                        <th>Produto</th>
                        <th className="center">Quantidade</th>
                        <th className="center">Ações</th>
                     </tr>
                  </thead>
                  <tbody>
                     {items.map((item, index) => {
                        const qtyEmpty =
                           String(item.prod_qtd ?? '').trim() === '' ||
                           Number(item.prod_qtd) < 1;
                        return (
                           <tr
                              key={`${item.prod_id}-${index}`} // eslint-disable-line react/no-array-index-key
                           >
                              <td className="center">
                                 {item.sku || item.prod_id || '—'}
                              </td>
                              <td>
                                 {item.prodName ||
                                    item.xml_name ||
                                    `Produto ${item.prod_id}`}
                              </td>
                              <td className="center">
                                 <QuantityStepper
                                    size="sm"
                                    value={item.prod_qtd}
                                    invalid={qtyEmpty}
                                    disabled={disabled}
                                    onChange={(value) =>
                                       handleRowQtyChange(index, value)
                                    }
                                    ariaLabel={`Quantidade de ${
                                       item.prodName ?? item.prod_id
                                    }`}
                                 />
                              </td>
                              <td className="center">
                                 <IconButton
                                    type="button"
                                    $intent="danger"
                                    disabled={disabled}
                                    onClick={() => handleRemove(index)}
                                    title="Remover item"
                                 >
                                    <FaTrashAlt size={14} />
                                 </IconButton>
                              </td>
                           </tr>
                        );
                     })}
                  </tbody>
                  <tfoot>
                     <TotalRow>
                        <td colSpan={2}>
                           Total · {items.length}{' '}
                           {items.length === 1 ? 'item' : 'itens'}
                        </td>
                        {/* Quantidade + Ações num só rodapé: célula vazia de
                            fechamento não tem rótulo e o a11y reclama, com
                            razão — não há o que anunciar nela. */}
                        <td className="center" colSpan={2}>
                           {totalUnits} un.
                        </td>
                     </TotalRow>
                  </tfoot>
               </DataTable>
            </TableWrapper>
         )}

         <ScanHint>
            <FaBarcode size={12} aria-hidden="true" />
            SKU de 7 dígitos resolve direto; Enter na quantidade adiciona o
            item. Produto repetido soma à linha existente.
         </ScanHint>
      </>
   );
}

MovementItemsEditor.propTypes = {
   items: PropTypes.arrayOf(
      PropTypes.shape({
         prod_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
         prod_qtd: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
         sku: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
         prodName: PropTypes.string,
      })
   ).isRequired,
   onChange: PropTypes.func.isRequired,
   disabled: PropTypes.bool,
   // Entrada/saída exigem PDV antes de montar a lista (o item carrega o PDV);
   // o editor já nasce com o PDV do movimento.
   requirePdv: PropTypes.bool,
   pdvId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
   emptyTitle: PropTypes.string,
   emptyDescription: PropTypes.string,
};
